import {
  Body1Strong,
  mergeClasses,
  makeStyles,
  tokens,
  shorthands,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type MenuButtonProps,
} from '@fluentui/react-components';
import { MenuButton } from '@shared/components/MenuButton';
import { TASK_STATUS_METADATA, TASK_STATUSES } from '../constants';
import type { ETaskStatus } from '../types';

// Match Fluent's secondary button geometry without button semantics or interaction.
const useStyles = makeStyles({
  readOnly: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    minWidth: '96px',
    ...shorthands.padding('5px', tokens.spacingHorizontalM),
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  small: {
    minWidth: '64px',
    cursor: 'not-allowed',
    ...shorthands.padding('3px', tokens.spacingHorizontalS),
    // Reserve the dropdown's 12px arrow slot and gap so list labels have the same width.
    paddingRight: `calc(${tokens.spacingHorizontalS} + 12px + ${tokens.spacingHorizontalXS})`,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  large: {
    ...shorthands.padding('8px', tokens.spacingHorizontalL),
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },
});

const TaskStatusMenu = ({
  status,
  disabled,
  onStatusChange,
  size,
}: {
  status: ETaskStatus;
  disabled: boolean;
  onStatusChange?: (status: ETaskStatus) => void;
  /** The list uses a small button so it fits the row; icons and options stay the same. */
  size?: MenuButtonProps['size'];
}) => {
  const metadata = TASK_STATUS_METADATA[status];
  const Icon = metadata.icon;
  const content = (
    <span className="flex items-center space-x-1">
      <Icon fontSize={size === 'small' ? 16 : 20} />
      <span>{metadata.label}</span>
    </span>
  );
  const classes = useStyles();
  return !onStatusChange ? (
    <Body1Strong
      className={mergeClasses(classes.readOnly, size === 'small' && classes.small, size === 'large' && classes.large)}
    >
      {content}
    </Body1Strong>
  ) : (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton appearance="secondary" size={size} disabled={disabled}>
          {content}
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {TASK_STATUSES.filter((option) => option.value !== status).map((option) => (
            <MenuItem key={option.value} icon={<option.icon />} onClick={() => onStatusChange(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
export default TaskStatusMenu;
